// dev-portal/src/layouts/MarketplaceLayout.jsx

import React, { useState, useEffect } from 'react';
import "../assets/css/style.css";
import { Link, useNavigate } from 'react-router-dom';
import ScrollToTop from "./ScrollToTop";
import {
    Package,
    User,
    LogIn,
    Home,
    Code,
    Shield,
    Menu,
    X,
    Users,
    ShoppingBag,
    LogOut
} from 'lucide-react';
import { api } from '../api/client';
import { useProfile } from '../contexts/ProfileContext';
import ProfileImage from '../components/shared/ProfileImage';

export default function MarketplaceLayout({ children }) {
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('role'); // Get user role from localStorage
    const { profile } = useProfile(); // Use profile context

       const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role'); // Also clear role
        navigate('/');
    };

    // Determine dashboard link and label based on role
    const getDashboardInfo = () => {
        if (userRole === 'admin') {
            return {
                link: '/admin',
                label: 'Admin Dashboard',
                icon: Shield
            };
        } else {
            return {
                link: '/dashboard',
                label: 'Developer Dashboard',
                icon: Home
            };
        }
    };

    const dashboardInfo = getDashboardInfo();

    const apiDocsUrl = process.env.REACT_APP_API_DOCS_URL || `${process.env.REACT_APP_API_BASE_URL}/docs` || '/docs';

    return (
        <div className="min-h-screen flex flex-col">
            {/* Navigation Header */}
              <ScrollToTop />
            <nav className="bg-white shadow-sm sticky top-0 z-50 den-bg-color">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 menu-container">
                    <div className="flex justify-between items-center h-20">
                        {/* Logo and Brand */}
                        <div className="flex items-center gap-8">
                            <Link to="/" className="flex items-center gap-2">
                                <Package className="w-8 h-8 text-blue-600 den-nav-color-icon" />
                                <span className="text-xl font-bold text-gray-900 den-nav-color">Dentulu Marketplace</span>
                            </Link>

                            {/* Navigation Links */}
                            <div className="hidden md:flex items-center gap-6">
                                <Link to="/marketplace/categories" className="text-gray-600 hover:text-gray-900 den-nav-color">
                                    Browse Apps
                                </Link>
                                {/*      <Link to="/marketplace/categories" className="text-gray-600 hover:text-gray-900">
                                    Categories
                                </Link> */}
                                <Link to="/marketplace/developers" className="text-gray-600 hover:text-gray-900 den-nav-color">Developers</Link>
                                <Link to="/marketplace/dental-professionals" className="text-gray-600 hover:text-gray-900 den-nav-color">Dental Professionals</Link>
                                {token && userRole === 'developer' && (
                                    <Link to="/marketplace/my-purchases" className="text-gray-600 hover:text-gray-900 den-nav-color">
                                        My Purchases
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* User Actions */}
                        <div className="flex items-center gap-4">
                            {/* Developer Portal Link - Only show for non-admin users */}
                            {userRole !== 'admin' && (
                                <Link
                                    to="/portal"
                                    className="hidden md:block flex items-center gap-2 text-gray-600 hover:text-gray-900 sign-btn"
                                    title="Developer Portal"
                                >
                                   
                                    <span className="hidden sm:inline">Developer Portal</span>
                                </Link>
                            )}

                            {token ? (
                                <>
                                    {/* Dashboard Quick Link */}
                                    <Link
                                        to={dashboardInfo.link}
                                        className="text-gray-600 hover:text-gray-900 den-nav-color"
                                        title={dashboardInfo.label}
                                    >
                                        <dashboardInfo.icon className="w-5 h-5" />
                                    </Link>

                                    {/* User Dropdown */}
                                    <div className="relative group hidden md:block">
                                        <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900 den-nav-color">
                                            <ProfileImage
                                                imageUrl={profile?.profile_image_url}
                                                alt="Profile"
                                                size="sm"
                                                cacheBust={true}
                                            />
                                            <span className="hidden sm:inline">
                                                {userRole === 'admin' ? 'Admin' : 'Account'}
                                            </span>
                                        </button>
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">

                                            {/* Dashboard Link */}
                                            <Link
                                                to={dashboardInfo.link}
                                                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                                            >
                                                {dashboardInfo.label}
                                            </Link>

                                            {/* Role-specific menu items */}
                                            {userRole === 'admin' ? (
                                                <>
                                                    <Link to="/admin/users" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        Manage Users
                                                    </Link>
                                                    <Link to="/admin/apps" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        Manage Apps
                                                    </Link>
                                                    <Link to="/admin/categories" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        Manage Categories
                                                    </Link>
                                                </>
                                            ) : (
                                                <>
                                                    <Link to="/dashboard/apps" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        My Apps
                                                    </Link>
                                                    <Link to="/dashboard/api-keys" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        API Keys
                                                    </Link>
                                                    <Link to="/marketplace/my-purchases" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                        My Purchases
                                                    </Link>
                                                </>
                                            )}

                                            <hr className="my-1" />
                                            <button
                                                onClick={handleLogout}
                                                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="hidden md:flex items-center gap-3">
                                    <Link
                                        to="/login"
                                        className="text-gray-600 hover:text-gray-900 sign-btn"
                                    >
                                        Sign In
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 sign-up-btn"
                                    >
                                        <LogIn className="w-4 h-4" />
                                        Sign Up
                                    </Link>
                                </div>
                            )}

                    {/* Mobile Menu Button */}
                            <button
                                className="md:hidden"
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <Menu className="w-7 h-7" />
                            </button>


                        </div>
                    </div>
                </div>
            </nav>


            {/* ================= MOBILE MENU ================= */}
{mobileMenuOpen && (
  <div className="mobile-menu-overlay">
    <div className="mobile-menu-panel">

      {/* Header */}
      <div className="mobile-menu-header">
        <span>Menu</span>
        <button onClick={() => setMobileMenuOpen(false)}>
          X {/* Close icon (you can replace with your icon component if needed) */}
        </button>
      </div>

      {/* Marketplace Section */}
      <div className="mobile-menu-section">
        <Link to="/marketplace/categories" onClick={() => setMobileMenuOpen(false)} className="menu-marketplace">
          Browse Apps
        </Link>
        <Link to="/marketplace/developers" onClick={() => setMobileMenuOpen(false)} className="menu-marketplace">
          Developers
        </Link>
        <Link to="/marketplace/dental-professionals" onClick={() => setMobileMenuOpen(false)} className="menu-marketplace">
          Dental Professionals
        </Link>
        {token && userRole === 'developer' && (
          <Link to="/marketplace/my-purchases" onClick={() => setMobileMenuOpen(false)} className="menu-marketplace">
            My Purchases
          </Link>
        )}
        
        {/* Developer Portal - Show for non-admin users or when not logged in */}
        {userRole !== 'admin' && (
          <Link to="/portal" onClick={() => setMobileMenuOpen(false)} className="menu-marketplace">
            Developer Portal
          </Link>
        )}
      </div>

      {/* Account Section */}
      <div className="mobile-menu-section">
        <h4>Account</h4>
        {token ? (
          <>
            {/* Dashboard */}
            <Link to={dashboardInfo.link} onClick={() => setMobileMenuOpen(false)} className="menu-dashboard">
              {dashboardInfo.label}
            </Link>

            {/* Role-specific links */}
            {userRole === 'admin' ? (
              <>
                <Link to="/admin/users" onClick={() => setMobileMenuOpen(false)} className="menu-admin">
                  Manage Users
                </Link>
                <Link to="/admin/apps" onClick={() => setMobileMenuOpen(false)} className="menu-admin">
                  Manage Apps
                </Link>
                <Link to="/admin/categories" onClick={() => setMobileMenuOpen(false)} className="menu-admin">
                  Manage Categories
                </Link>
              </>
            ) : (
              <>
                <Link to="/dashboard/apps" onClick={() => setMobileMenuOpen(false)} className="menu-developer">
                  My Apps
                </Link>
                <Link to="/dashboard/api-keys" onClick={() => setMobileMenuOpen(false)} className="menu-developer">
                  API Keys
                </Link>
                <Link to="/marketplace/my-purchases" onClick={() => setMobileMenuOpen(false)} className="menu-developer">
                  My Purchases
                </Link>
              </>
            )}

            {/* Logout */}
            <button onClick={handleLogout} className="mobile-logout-btn">
              Logout
            </button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="mobile-signup-btn">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="mobile-signup-btn">
              Sign Up
            </Link>
          </>
        )}
      </div>
    </div>
  </div>
)}

            {/* Main Content */}
            <main className="flex-1">
                {children}
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 menu-container">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <h3 className="text-lg font-semibold mb-3">Dentulu Marketplace</h3>
                            <p className="text-gray-400">
                                Discover and integrate powerful applications to enhance your dental practice.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-3">For Developers</h3>
                            <ul className="space-y-2 text-gray-400">
                                <li><Link to="/portal" className="hover:text-white">Developer Portal</Link></li>
                                <li><Link to="/signup" className="hover:text-white">Become a Developer</Link></li>
                                <li>
                                    <Link
                                        to="/api-docs"
                                        className="hover:text-white"
                                    >
                                        API Documentation
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-3">Support</h3>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="https://help.dentulu.com/" className="hover:text-white">Help Center</a></li>
                                <li><a href="/terms" className="hover:underline">Terms of Service</a></li>
                                <li><a href="/privacy-policy" className="hover:underline">Privacy Policy</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
                        <p>&copy; {new Date().getFullYear()} Dentulu. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}